import { toast } from "sonner";
import { FilePenLine, Loader2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ReactMarkdown from "react-markdown";

import { Button } from "src/shadcn/ui/button";
import { Loading } from "src/components/common";
import { Textarea } from "src/shadcn/ui/textarea";
import { IResume, IJob } from "src/models";
import {
  generateCoverLetterResume,
  updateCoverLetterResume,
} from "src/services/job";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "src/shadcn/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/shadcn/ui/tooltip";

interface Props {
  resumeId: string;
  currentJob: IJob;
}

const TailoredCoverLetter = ({ resumeId, currentJob }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  // states
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [coverLetter, setCoverLetter] = useState<IResume | null>(null);

  // functions
  const handleGenerateCoverLetter = async () => {
    setIsLoading(true);
    const res = await generateCoverLetterResume(currentJob?.id, resumeId);
    setIsLoading(false);

    if (res) {
      setCoverLetter({ ...res });
    }
  };

  const handleUpdateCoverLetter = async () => {
    setIsUpdating(true);
    const res = await updateCoverLetterResume(
      currentJob?.id,
      resumeId,
      coverLetter?.cover_letter ?? "",
    );
    setIsUpdating(false);

    if (res) {
      setCoverLetter({ ...res });
      setIsEdit(false);
      toast.success("Update cover letter successfully");
    }
  };

  const handleBack = () => {
    navigate(`${location.pathname}?step=4&tab=revised_resume`);
  };

  const handleNextStep = () => {
    navigate(`${location.pathname}?step=6`);
  };

  const handleCoverLetterChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setCoverLetter((prev) =>
      prev ? { ...prev, cover_letter: e.target.value } : null,
    );
  };

  // memos
  const isDisabledBtn = useMemo(() => {
    return isLoading || isUpdating;
  }, [isLoading, isUpdating]);

  // effects
  useEffect(() => {
    if (resumeId && currentJob) {
      const resumes = currentJob?.resumes ?? [];
      const resumeActivate = resumes.find(
        (resume) => resume?.active && resume?.id === resumeId,
      );

      if (resumeActivate) {
        setCoverLetter({ ...resumeActivate });

        if (!resumeActivate?.cover_letter) {
          setIsEdit(true);
        }
      }
    }
  }, [resumeId, currentJob]);

  return (
    <div className="w-full h-full relative pb-14 grow">
      {!isLoading ? (
        <Card className="p-4">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="text-lg hidden">
              Tailored Cover Letter
            </CardTitle>
            <CardDescription className="hidden">123</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            {isEdit ? (
              <Textarea
                value={coverLetter?.cover_letter ?? ""}
                placeholder='Click "Write Cover Letter" for a tailored cover letter'
                rows={28}
                onChange={handleCoverLetterChange}
              />
            ) : (
              <ReactMarkdown
                // eslint-disable-next-line react/no-children-prop
                children={coverLetter?.cover_letter}
                remarkPlugins={[remarkGfm]}
                className="render-markdown"
                skipHtml={false}
                rehypePlugins={[rehypeRaw]}
              />
            )}

            <div className="flex mt-6 justify-end items-center gap-2">
              {isEdit && (
                <Button
                  variant="ghost"
                  disabled={isUpdating}
                  className="flex items-center gap-1"
                  onClick={handleUpdateCoverLetter}
                >
                  {isUpdating && <Loader2 className="animate-spin size-4" />}
                  <span>Save change</span>
                </Button>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      disabled={false}
                      className="p-1 h-fit"
                      onClick={() => setIsEdit(!isEdit)}
                    >
                      <FilePenLine className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="dark:text-white text-black text-sm">
                    Edit cover letter
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="w-full h-[40vh] flex justify-center items-center">
          <Loading />
        </div>
      )}

      <div className="flex justify-end gap-3 absolute bottom-0 right-0">
        <Button
          disabled={isDisabledBtn}
          variant="outline"
          className=""
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          disabled={isDisabledBtn}
          onClick={handleGenerateCoverLetter}
        >
          Write cover letter
        </Button>
        <Button
          disabled={isDisabledBtn}
          onClick={handleNextStep}
        >
          Next Step
        </Button>
      </div>
    </div>
  );
};

export default TailoredCoverLetter;
